import { ApplicationState } from "../store/nissan_no";

import {
  DataLayerProps,
  EnvironmentInfoProps,
  PageInfoProps,
  CustomerInfoProps,
  ProductInfoProps,
  ProcessInfoProps,
  ObjectInfoProps,
  InsuranceInfoProps,
  EcommerceProps,
  ILocalValidation,
} from "./";
import { GetSelectedAddonsForCoverage } from "../store/AddOn";
import { format } from "date-fns";
import { BrandsEnum } from "../enums";
import { Country } from "..";

function getEnvInfo(data: ApplicationState): EnvironmentInfoProps {
  const partner = data.inputs.partner as BrandsEnum;
  const commonInfo = {
    country: "Norway" as Country,
    businessArea: "PRI" as EnvironmentInfoProps["businessArea"],
  };

  const brand = getBrandFromPartner(partner).toLocaleLowerCase();
  switch (partner) {
    case BrandsEnum.VolviaNo:
      return {
        ...commonInfo,
        portfolio: "VRF",
        partner: brand,
      };
    case BrandsEnum.NissanNo:
      return {
        ...commonInfo,
        portfolio: "IMF",
        partner: brand,
      };
    default:
      throw new Error(`Unsupported brand: ${partner}`);
  }
}

function getPageInfo(): PageInfoProps {
  return {
    pageType: "Webshop Page",
    pageLanguage: "NO",
    pageUrl: window.location.href,
    canonicalPageUrl: undefined,
    httpStatusCode: "200",
  };
}

function getCustomerInfo(data: ApplicationState): CustomerInfoProps {
  const customer = data.profile;
  const damageFree = data.damageFreeYear;
  const insuranceReceive = data.insurance?.insuranceReceive;

  let isECustomer = customer?.isECustomer == null ? undefined : customer.isECustomer;
  if (insuranceReceive) {
    isECustomer = insuranceReceive.selectedValue == null ? undefined : insuranceReceive.selectedValue === "W";
  }

  return {
    customerId: {
      id: undefined,
      type: "SSN",
    },
    age: undefined,
    gender: undefined,
    customerStatus: [
      {
        type: "if",
        value: "Unknown",
      },
      {
        type: "carPartners",
        value: "Unknown",
      },
    ],
    customerDiscounts: undefined,
    isObjectPossessor: undefined,
    loggedIn: false,
    campaignCode: undefined,
    fullname: undefined,
    firstname: undefined,
    lastname: undefined,
    recentClaims: damageFree?.selected == null ? undefined : !damageFree.selected ? "Yes" : "No",
    eCustomer: isECustomer ? "Yes" : "No",
    phone: undefined,
    email: undefined,
  };
}

function getProductInfo(): ProductInfoProps {
  return {
    productName: {
      localName: "Bil",
      localSubname: undefined,
      name: undefined,
      subname: undefined,
    },
    currencyCode: "NOK",
  };
}

function getProcessInfo(data: ApplicationState, mainStepName: string): ProcessInfoProps {
  const status: "available" | "not available" = "not available";

  return {
    name: "webshop",
    processStep: {
      mainStep: mainStepName as any,
      subStep: undefined,
      status: status,
    },
  };
}

function getBrandFromPartner(partner: BrandsEnum): string {
  switch (partner) {
    case BrandsEnum.VolviaNo:
      return "Volvia";
    case BrandsEnum.NissanNo:
      return "Nissan";
    default:
      throw new Error(`Unsupported brand: ${partner}`);
  }
}

function getObjectInfo(data: ApplicationState): ObjectInfoProps {
  const obj = data.profile;
  const partner = data.inputs.partner as BrandsEnum;
  const brand = getBrandFromPartner(partner).toLocaleLowerCase();
  return {
    objectId: {
      id: undefined,
      type: "REG",
    },
    brand: brand,
    model: obj?.carModel,
    year: obj?.carYear,
    objectSize: {
      value: undefined,
      unit: "kg",
    },
  };
}

function getInsuranceInfo(data: ApplicationState): InsuranceInfoProps {
  const inputs = data.inputs;
  const insurance = data.insurance;
  const distance = data.distance;
  const addons = data.addon;
  const youngDriver = data.youngDriver;
  const bonus = data.bonus;

  const selectedCoverage = insurance?.coverage?.Coverages?.find(elem => elem.InternalCode === insurance.coverage?.selectedCode);

  const selectedDeductible =
    insurance?.coverage?.SelectedDeductible ?? selectedCoverage?.Deductibles?.find(d => d.IsSelected === true)?.Code;

  const primaryPrice = selectedCoverage?.PrimaryPrice;

  const insurancePrice: number = GetYearlyPrice(primaryPrice?.Amount, addons?.addOns);

  // // start of dataLayer

  const insuranceDeductable = GetDeductibleNumber(selectedDeductible);

  const selectedAddonsForCoverage = GetSelectedAddonsForCoverage(addons, insurance);
  const selectedAddons =
    selectedAddonsForCoverage
      .map(x => addons?.addOns?.find(elem => elem.InternalCode === x)?.Name)
      ?.filter(x => x != null)
      ?.map(x => ({ value: x })) ?? [];

  if (selectedAddons.length === 0) {
    selectedAddons.push({ value: "" });
  }
  return {
    price: Number(insurancePrice),
    quoteId: inputs?.quoteId,
    entranceType: "null",
    coverage: selectedCoverage?.LongName,
    insuranceType: {
      name: "fixed",
      movingPrice: undefined,
      fixedPrice: undefined,
    }, // insuranceType is only relevant for volvia. For any other webshops, insuranceType can be left out.
    insuranceDeductable: [
      {
        type: "product",
        value: insuranceDeductable,
      },
    ],
    coveredAmount: undefined,
    mileage: distance?.values.find((mileage: any) => mileage.id === distance.selectedId)?.title,
    insuranceAddons: selectedAddons,
    youngDriver:
      !youngDriver?.questionVisible || youngDriver?.selected === null || youngDriver?.selected === undefined
        ? undefined
        : youngDriver?.values
          .find(state => state.value === youngDriver?.selected)
          ?.text.toLowerCase()
          .startsWith("n") !== true,
    bonus: [{ type: "General", value: bonus?.bonus?.selectedId }],
    startdate: insurance?.startDate?.value ? format(insurance.startDate.value, "yyyy-MM-dd") : undefined,
    insurancePayment: {
      invoice: undefined,
      period: "monthly",
    },
    signingId: undefined,
    ehdId: undefined,
  };
}

function getEcommerceInfo(data: ApplicationState): EcommerceProps {
  const insurance = getInsuranceInfo(data);
  const product = getProductInfo();
  const partner = data.inputs.partner as BrandsEnum;
  const brand = getBrandFromPartner(partner).toLocaleLowerCase();

  return {
    currencyCode: "NOK",
    purchase: {
      actionField: {
        id: new Date().toDateString() + Math.random(),
        affiliation: brand,
        revenue: insurance.price,
      },
      products: [
        {
          id: "Bil",
          name: product?.productName?.localName,
          price: insurance.price,
          brand: brand,
          category: undefined,
          variant: insurance?.coverage,
          quantity: 1,
        },
      ],
    },
  };
}

function GetYearlyPrice(primaryPrice: any, addons: any) {
  let fullprice = Number(primaryPrice);
  if (addons !== undefined) {
    let addonPrice = 0;
    addons.forEach(function (value: any) {
      if (value.Checked) {
        const priceNumber = value.MonthlyPrice.replace("kr/md.", "").trim();
        addonPrice = addonPrice + Number(priceNumber);
      }
    });
    fullprice = fullprice + addonPrice;
  }
  return fullprice * 12;
}

function GetDeductibleNumber(selectedDeductible: any) {
  if (selectedDeductible) {
    const deductible = selectedDeductible?.includes("D") ? selectedDeductible.replace("D", "").trim() : selectedDeductible.trim();
    return Number(deductible);
  }
}

export function getPageLoadModelNO(data: ApplicationState, eventType = "Page Load", processMainStepName = "price"): DataLayerProps {
  return {
    event: eventType,
    environmentInfo: getEnvInfo(data),
    pageInfo: getPageInfo(),
    customerInfo: getCustomerInfo(data),
    productInfo: getProductInfo(),
    processInfo: getProcessInfo(data, processMainStepName),
    objectInfo: getObjectInfo(data),
    //insuranceInfo: getInsuranceInfo(data),
  };
}

export function getServerErrorModelNO(data: ApplicationState): DataLayerProps {
  const customerInfo = getCustomerInfo(data);
  return {
    event: "Web Shop Server Error",
    customerInfo: {
      customerId: customerInfo.customerId,
      age: customerInfo.age,
      gender: customerInfo.gender,
      customerStatus: customerInfo.customerStatus,
      customerDiscounts: customerInfo.customerDiscounts,
      isObjectPossessor: customerInfo.isObjectPossessor,
    },
    processInfo: getProcessInfo(data, "error"),
    objectInfo: getObjectInfo(data),
    insuranceInfo: {
      errorCode: "Server - 500",
    },
  };
}

export function getLocalValidationErrorModelNO(data: ApplicationState, validationError: ILocalValidation): DataLayerProps {
  const customerInfo = getCustomerInfo(data);
  return {
    event: "Web Shop Format Error",
    customerInfo: {
      customerId: customerInfo.customerId,
      age: customerInfo.age,
      gender: customerInfo.gender,
      customerStatus: customerInfo.customerStatus,
      customerDiscounts: customerInfo.customerDiscounts,
      isObjectPossessor: customerInfo.isObjectPossessor,
    },
    processInfo: getProcessInfo(data, "error"),
    objectInfo: getObjectInfo(data),
    insuranceInfo: {
      errorCode: `Format - ${validationError.fieldName} - ${validationError.state}`,
    },
  };
}

export function getDataUpdateModelNO(data: ApplicationState): DataLayerProps {
  return {
    event: "Insurance Information Updated",
    insuranceInfo: getInsuranceInfo(data),
    customerInfo: getCustomerInfo(data),
  };
}
export function getEcommerceModelNO(data: ApplicationState, eventName: string): DataLayerProps {
  return {
    event: eventName,
    ecommerce: getEcommerceInfo(data),
  };
}
